import React, { useEffect, useRef, useState } from 'react';
import { Container, Card, CardContent, Typography, Button, Badge } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DayCalendarSkeleton, LocalizationProvider } from '@mui/x-date-pickers';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import Grid from '@mui/material/Grid2';

import axios from '@shared/config/axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MAINTENANCE_STATUSES, MAINTENANCE_TYPE } from '@shared/api/constants';
import { Alert } from '@features/alert';
import { ProgressBar } from '@features/progress-bar';
import { LoadingPage } from '@widgets/loading-page';

dayjs.extend(utc);

function ServerDay(props) {
    const { highlightedDays = [], day, outsideCurrentMonth, onClick, ...other } = props;

    const isSelected = !outsideCurrentMonth &&
        highlightedDays.includes(day.toISOString().split('T')[0]); // Приводим дату к формату YYYY-MM-DD


    // Обработчик клика, который передает правильную дату
    const handleClick = (event) => {
        onClick(day, event); // передаем саму дату и событие
    };

    return (
        <Badge
            key={day.toString()}
            overlap="circular"
            badgeContent={isSelected ? '🔴' : undefined}
        >
            <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} onClick={handleClick}/>
        </Badge>
    );
}


export const HomePage = () => {
    const alertRef = useRef();

    const [tasks, setTasks] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState(null);
    const [highlightedDays, setHighlightedDays] = React.useState([]);

    // Получение задач
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['tasks'],
        queryFn: async () => {
            const response = await axios.get('/maintenance');
            return response.data;
        },
        refetchOnWindowFocus: true,
        retry: false,
    });

    const formatDatesForCalendar = (tasks) => {
        return tasks
            .filter((task) => task?.intervalType === MAINTENANCE_TYPE.DAY && task?.nextDue) // Фильтруем задачи с интервалом "day"
            .map((task) => new Date(task?.nextDue).toISOString().split('T')[0]); // Форматируем в YYYY-MM-DD
    };

    const handleDateClick = (date) => {
        const formattedDate = date.toISOString().split('T')[0]; // Преобразуем в строку с датой
        const tasksForDate = tasks.filter(
            (task) => task.nextDue && new Date(task.nextDue).toISOString().split('T')[0] === formattedDate
        );
        setSelectedDate(formattedDate);
        setSelectedTasks(tasksForDate);
    };

    const dateNow = dayjs().toISOString().split('T')[0];

    useEffect(() => {
        if (data) {
            const daysForTasks = formatDatesForCalendar(data);

            setTasks(data);
            setHighlightedDays(daysForTasks);
            setSelectedDate(dateNow);
        }
        // eslint-disable-next-line
    }, [data]);

// Отслеживаем изменения selectedDate, чтобы автоматически вызвать handleDateClick
    useEffect(() => {
        if (selectedDate === dateNow) {
            handleDateClick(dayjs());
        }
        // eslint-disable-next-line
    }, [selectedDate]);

    // Мутация для завершения задачи
    const approveTaskMutation = useMutation({
        mutationFn: async (taskId) => {
            await axios.patch(`/maintenance/${taskId}`, {
                currentCount: 0,
                currentHours: 0,
                status: MAINTENANCE_STATUSES.COMPLETED
            });
        },
        onSuccess: (data, variables) => {
            setTasks((prevTasks) =>
                prevTasks.map((task) =>
                    task._id === variables ? {
                        ...task,
                        currentCount: 0,
                        currentHours: 0,
                        status: MAINTENANCE_STATUSES.COMPLETED
                    } : task
                )
            );
            refetch();
            alertRef.current.handleOpen('success', 'maintenance task approved');
        },
    });

    if (isLoading) return <LoadingPage/>;

    return (
        <Container
            className="no-scrollbar"
            sx={{ width: '100%', height: '100%', padding: 2, overflowY: 'auto' }}
        >
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateCalendar
                            timezone="UTC"
                            date={selectedDate}
                            views={['day']}
                            onChange={(date) => {
                                setSelectedDate(date);
                            }}
                            renderLoading={() => <DayCalendarSkeleton/>}
                            slots={{
                                day: ServerDay,
                            }}
                            slotProps={{
                                day: {
                                    highlightedDays,
                                    onClick: handleDateClick, // Передаем обработчик
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                {/* Блок для отображения задач на выбранную дату */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="h6" sx={{ marginTop: 2 }}>
                        tasks for {selectedDate}
                    </Typography>
                    {highlightedDays.includes(selectedDate) && selectedTasks?.map((task) => (
                        <Card key={task._id} sx={{ marginTop: 2 }}>
                            <CardContent>
                                <Typography variant="h6">{task.task}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {task.description || '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>interval:</strong> {task.intervalValue} {task.intervalType}
                                </Typography>
                                {task.status === MAINTENANCE_STATUSES.PENDING && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ mt: 1 }}
                                        onClick={() => approveTaskMutation.mutate(task._id)}
                                        disabled={approveTaskMutation.isLoading}
                                    >
                                        complete
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Grid>
                <Grid size={12}>
                    <Typography variant="h5" gutterBottom sx={{ borderBottom: '2px solid #ccc', width: '100%' }}>
                        maintenance tasks
                    </Typography>
                </Grid>
                {tasks.filter((task) => task.intervalType !== MAINTENANCE_TYPE.DAY).map((task) => (
                    <Grid key={task._id} size={{ xs: 12, sm: 6 }}>
                        <Card key={task._id} sx={{ marginTop: 2 }}>
                            <CardContent>
                                <Typography variant="h6">{task.task}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {task.description || '-'}
                                </Typography>
                                <ProgressBar
                                    intervalValue={task?.intervalValue}
                                    currentCount={
                                        ( task.intervalType === MAINTENANCE_TYPE.COUNT && task?.currentCount )
                                        || task?.currentHours?.toFixed(3)
                                    }
                                />
                                {task.intervalType === MAINTENANCE_TYPE.COUNT && (
                                    <>
                                        <Typography variant="body2">
                                            <span><strong>interval:</strong> {task.intervalValue} count</span>
                                        </Typography>
                                        <Typography variant="body2">
                                            <span><strong>current count:</strong> {task.currentCount}</span>
                                        </Typography>

                                    </>
                                )}
                                {task.intervalType === MAINTENANCE_TYPE.HOUR && (
                                    <>
                                        <Typography variant="body2">
                                            <strong>interval:</strong> {task.intervalValue} h
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>current hours:</strong> {task.currentHours.toFixed(3)} h
                                        </Typography>
                                    </>
                                )}
                                {task.status === MAINTENANCE_STATUSES.PENDING && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => approveTaskMutation.mutate(task._id)}
                                        disabled={approveTaskMutation.isLoading}
                                        sx={{ mt: 1 }}
                                    >
                                        Complete
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Alert ref={alertRef}/>
        </Container>
    );
};
